
import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator"
import CalendarView from "@/components/Config/Schedules/CalendarView.vue"
// @ts-ignore
import Calendar from "v-calendar/lib/components/calendar.umd"
// @ts-ignore
import DatePicker from "v-calendar/lib/components/date-picker.umd"
import { DayOfWeek } from "@/types/schedule"
import { Getter } from "vuex-class"
import { TenantFeatures } from "@/types/tenantFeatures"
import cloneDeep from "lodash/cloneDeep"
import { DateTime } from "luxon"

@Component({
	components: {
		"calendar": CalendarView,
		"v-calendar": Calendar,
		"v-date-picker": DatePicker
	}
})
export default class MultiDatePicker extends Vue {
	@Prop()
	value!: Date[] | null;

	@Prop()
	startDate!: Date | null

	@Prop()
	endDate!: Date | null

	@Prop({ type: Boolean, default: false })
	initialExcludeWeekends!: boolean;

	@Getter
	getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	startDateUTC: Date | null = null;
	endDateUTC: Date | null = null;

	selectedDates: Date[] = []

	areAllMondaysSelected: boolean = false
	areAllTuesdaysSelected: boolean = false
	areAllWednesdaysSelected: boolean = false
	areAllThursdaysSelected: boolean = false
	areAllFridaysSelected: boolean = false
	areAllSaturdaysSelected: boolean = false
	areAllSundaysSelected: boolean = false
	areAllWeekdaysSelected: boolean = false
	isExcludeWeekendsSelected: boolean = false

	isMondayQuickOptionDisabled: boolean = false
	isTuesdayQuickOptionDisabled: boolean = false
	isWednesdayQuickOptionDisabled: boolean = false
	isThursdayQuickOptionDisabled: boolean = false
	isFridayQuickOptionDisabled: boolean = false
	isSaturdayQuickOptionDisabled: boolean = false
	isSundayQuickOptionDisabled: boolean = false
	isWeekdayQuickOptionDisabled: boolean = false
	isExcludeWeekendsQuickOptionDisabled: boolean = false

	hasInvalidSelectedDate: boolean = false

	dayNumberIsAWeekend: ((dayNumber: number) => boolean) = (dayNumber: number) => [DayOfWeek.Saturday, DayOfWeek.Sunday].includes(dayNumber)

	@Watch("startDate")
	onStartDateChanged () {
		if (this.startDate === null) {
			this.startDateUTC = null
		} else {
			this.startDateUTC = this.convertDateToUTCDateMidnight(this.startDate)
		}

		this.dateRangeChanged()
	}

	@Watch("endDate")
	onEndDateChanged () {
		if (this.endDate === null) {
			this.endDateUTC = null
		} else {
			this.endDateUTC = this.convertDateToUTCDateMidnight(this.endDate, true)
		}

		this.dateRangeChanged()
	}

	mounted () {
		if (this.startDate !== null) this.startDateUTC = this.convertDateToUTCDateMidnight(new Date(this.startDate))
		if (this.endDate !== null) this.endDateUTC = this.convertDateToUTCDateMidnight(new Date(this.endDate), true)

		if (this.value) {
			let dateListLocal: Date[] = []

			this.value.forEach(date => {
				let dateUTC = this.convertDateToUTCDateMidnight(new Date(date))
				dateListLocal.push(dateUTC)
			})

			this.selectedDates = dateListLocal
		}

		// Set initial quick option values
		this.updateAllQuickOptions(true)

		if (this.initialExcludeWeekends && this.excludeWeekendsForCustomDatesFeatureIsEnabled) {
			this.setExcludeWeekends(true)
		}

		this.pushValidSelectedDates()
	}

	convertDateToUTC (date: Date) {
		return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds())
	}

	/**
	 * Converts a date to the same date in UTC at midnight.
	 * @param date The date to convert.
	 * @param dayEnd Whether to set the time to the end of the day (23:59:59). If false, it will be set to the start of the day (00:00:00).
	 */
	convertDateToUTCDateMidnight (date: Date, dayEnd: boolean = false) {
		const timeValueHrs = dayEnd ? 23 : 0
		const timeValueRest = dayEnd ? 59 : 0
		return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), timeValueHrs, timeValueRest, timeValueRest)
	}

	get getCalendarAttributes () {
		// If invalid dates are entered, return empty array.
		if (this.startDateUTC === null || this.endDateUTC === null) {
			return []
		}

		var attributes: any[] = []

		// Map the selected dates
		this.selectedDates.map(
			(d: Date) => {
				// Selected date is within the provided Start & End dates, and not a weekend if exclude weekends is selected
				if (new Date(d).getTime() >= new Date(this.startDateUTC!).getTime() &&
					new Date(d).getTime() <= new Date(this.endDateUTC!).getTime() &&
					!(this.isExcludeWeekendsSelected && this.dayNumberIsAWeekend(new Date(d).getDay()))) {
					attributes.push({
						highlight: true,
						dates: d
					})
				} else {
					// Selected date is outside the provided Start & End dates
					attributes.push({
						highlight: "red",
						dates: d
					})
				}
			}
		)
		return attributes
	}

	get getOutOfRangeDatesCheck () {
		// If invalid dates are entered, return false.
		if (this.startDateUTC === null || this.endDateUTC === null) {
			return false
		}

		return this.selectedDates.some(c => new Date(c).getTime() < new Date(this.startDateUTC!).getTime() || new Date(c).getTime() > new Date(this.endDateUTC!).getTime())
	}

	get getInvalidExcludedWeekendsSelectionCheck (): boolean {
		if (!this.isExcludeWeekendsSelected) {
			return false
		}
		return this.selectedDates.some(c => this.dayNumberIsAWeekend(new Date(c).getDay()))
	}

	get getValidSelectedDates () {
		// If invalid dates are entered, return empty array.
		if (this.startDateUTC === null || this.endDateUTC === null) {
			return []
		}

		// Return selected dates that are within the provided start & end date
		// This is for scenarios where start/end dates of a schedule are changed after dates have already been selected
		return this.selectedDates.filter(
			(d: Date) =>
				new Date(d).getTime() >= new Date(this.startDateUTC!).getTime() &&
				new Date(d).getTime() <= new Date(this.endDateUTC!).getTime() &&
				!(this.isExcludeWeekendsSelected && this.dayNumberIsAWeekend(new Date(d).getDay()))
		)
	}

	pushValidSelectedDates () {
		let selectedDates: Date[] = cloneDeep(this.getValidSelectedDates)
		let dateListUTC: Date[] = []

		selectedDates.forEach(date => {
			// Convert date to UTC using Luxon
			let dateUTC = DateTime.utc(
				date.getFullYear(),
				date.getMonth() + 1,
				date.getDate(),
				date.getHours(),
				date.getMinutes()
			).toJSDate()

			dateListUTC.push(dateUTC)
		})

		// Emit list of UTC dates
		this.$emit("input", dateListUTC)
	}

	get getWeekdayOptionCheckedValue () {
		return this.areAllWeekdaysSelected
	}

	updateQuickOptionValue (date: any) {
		const dayNumber = date.getDay()
		switch (dayNumber) {
			case DayOfWeek.Monday:
				this.areAllMondaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Monday)
				break
			case DayOfWeek.Tuesday:
				this.areAllTuesdaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Tuesday)
				break
			case DayOfWeek.Wednesday:
				this.areAllWednesdaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Wednesday)
				break
			case DayOfWeek.Thursday:
				this.areAllThursdaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Thursday)
				break
			case DayOfWeek.Friday:
				this.areAllFridaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Friday)
				break
			case DayOfWeek.Saturday:
				this.areAllSaturdaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Saturday)
				break
			case DayOfWeek.Sunday:
				this.areAllSundaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Sunday)
				break
			default:
		}
	}

	areAllWeekdayDatesSelected () {
		// If invalid dates are entered, return false
		if (this.startDateUTC == null || this.endDateUTC === null || this.startDateUTC! > this.endDateUTC!) {
			return false
		}

		// Set the base date to the start date
		let baseDate = new Date(this.startDateUTC!)

		// Initialise check variable for if a weekday exists
		let weekdayExists = false

		// Check if base date is selected if it's not a weekend
		if (!this.dayNumberIsAWeekend(baseDate.getDay())) {
			const index = this.getValidSelectedDates.findIndex((d: Date) => new Date(d).getTime() === baseDate.getTime())
			if (index < 0) {
				// Date isn't selected, therefore all weekday dates are not selected
				return false
			}
			// At least one weekday exists
			weekdayExists = true
		}

		var rollingDate = new Date(baseDate)

		// Loop through each instance of the provided day, starting at the one after the base date
		while (new Date(rollingDate.setDate(rollingDate.getDate() + 1)) <= this.endDateUTC!) {
			// Skip if day is a weekend
			if (this.dayNumberIsAWeekend(rollingDate.getDay())) {
				continue
			}

			// At least one weekday exists
			weekdayExists = true

			const index = this.selectedDates.findIndex((d: Date) => new Date(d).getTime() === rollingDate.getTime())
			if (index < 0) {
				// Date isn't selected, therefore all weekday dates are not selected
				return false
			}
		}

		if (weekdayExists) {
			// All weekday dates are selected
			return true
		} else {
			// No weekdays exist between the start & end dates
			return false
		}
	}

	areAllDatesSelectedByDay (dayNumber: number, initialLoad: boolean = false) {
		// If invalid dates are entered, return false.
		if (this.startDateUTC === null || this.endDateUTC === null) {
			return false
		}

		// Check if all weekdays are selected
		if (!initialLoad) {
			this.areAllWeekdaysSelected = this.areAllWeekdayDatesSelected()
		}

		let baseDate: Date = new Date(this.startDateUTC!)

		// Calculate the number of days to add to the base date
		var daysToAdd = dayNumber - baseDate.getDay()
		daysToAdd = daysToAdd < 0 ? daysToAdd + 7 : daysToAdd

		// Set the base date to the first date required based on the start date and the provided day number
		baseDate.setDate(baseDate.getDate() + (daysToAdd))

		// Check if the base date starts within the start & end dates
		if (baseDate.getTime() >= new Date(this.startDateUTC!).getTime() &&
			baseDate.getTime() <= new Date(this.endDateUTC!).getTime()) {
			// Check if the provided day starts on the startDate
			const initialIndex = this.getValidSelectedDates.findIndex((d: Date) => new Date(d).getTime() === baseDate.getTime())
			if (initialIndex < 0) {
				// Date isn't selected, therefore all dates are not selected for provided day
				return false
			}
		} else {
			// Base date is outside the start & end date range, therefore date is not selected
			return false
		}

		var rollingDate = new Date(baseDate)

		// Loop through each instance of the provided day, starting at the one after the base date
		while (new Date(rollingDate.setDate(rollingDate.getDate() + 7)) <= this.endDate!) {
			// Check if the date exists in the list of selected dates
			const index = this.getValidSelectedDates.findIndex((d: Date) => new Date(d).getTime() === rollingDate.getTime())
			if (index < 0) {
				// Date isn't selected, therefore all dates are not selected for provided day
				return false
			}
		}
		// All available dates are selected
		return true
	}

	onDayClick (day: any) {
		// Check if the selected date has already been selected (convert to time before comparison)
		const index = this.selectedDates.findIndex((d: Date) => new Date(d).getTime() === day.date.getTime())
		if (index >= 0) {
			// Remove date
			this.selectedDates.splice(index, 1)
		} else {
			// Do not allow selection of date if it's outside of the valid date range
			if (day.date < this.startDateUTC! || day.date > this.endDateUTC! ||
					(this.isExcludeWeekendsSelected && this.dayNumberIsAWeekend(new Date(day.date).getDay()))) {
				return
			}
			// Add date
			this.selectedDates.push(day.date)
		}

		// Update quick option check value
		this.updateQuickOptionValue(day.date)

		// Push date changes
		this.pushValidSelectedDates()
	}

	dateRangeChanged () {
		// The selected dates may no longer be valid based off the new startDate value, so update value for selected dates
		this.pushValidSelectedDates()

		this.updateAllQuickOptions()
	}

	updateAllQuickOptions (initialLoad: boolean = false) {
		this.areAllMondaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Monday, initialLoad)
		this.areAllTuesdaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Tuesday, initialLoad)
		this.areAllWednesdaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Wednesday, initialLoad)
		this.areAllThursdaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Thursday, initialLoad)
		this.areAllFridaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Friday, initialLoad)
		this.areAllSaturdaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Saturday, initialLoad)
		this.areAllSundaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Sunday, initialLoad)
		this.areAllWeekdaysSelected = this.areAllWeekdayDatesSelected()

		this.isMondayQuickOptionDisabled = this.disableQuickOptions(DayOfWeek.Monday)
		this.isTuesdayQuickOptionDisabled = this.disableQuickOptions(DayOfWeek.Tuesday)
		this.isWednesdayQuickOptionDisabled = this.disableQuickOptions(DayOfWeek.Wednesday)
		this.isThursdayQuickOptionDisabled = this.disableQuickOptions(DayOfWeek.Thursday)
		this.isFridayQuickOptionDisabled = this.disableQuickOptions(DayOfWeek.Friday)
		this.isSaturdayQuickOptionDisabled = this.disableQuickOptions(DayOfWeek.Saturday)
		this.isSundayQuickOptionDisabled = this.disableQuickOptions(DayOfWeek.Sunday)

		this.isWeekdayQuickOptionDisabled = (this.isMondayQuickOptionDisabled &&
											this.isTuesdayQuickOptionDisabled &&
											this.isWednesdayQuickOptionDisabled &&
											this.isThursdayQuickOptionDisabled &&
											this.isFridayQuickOptionDisabled)
	}

	disableQuickOptions (dayNumber: number) {
		if (this.isExcludeWeekendsSelected && this.dayNumberIsAWeekend(dayNumber)) {
			return true
		}

		let baseDate: Date = new Date(this.startDateUTC!)

		// Set the base date to the first date required date based on the start date and the provided day number
		baseDate.setDate(baseDate.getDate() + (dayNumber - baseDate.getDay()))
		// Check if the base date starts within the start & end dates
		if (baseDate.getTime() >= this.startDateUTC!.getTime() &&
			baseDate.getTime() <= this.endDateUTC!.getTime()) {
			// Day is within start & end dates, so enable quick option
			return false
		}

		var rollingDate = new Date(baseDate)

		// Loop through each instance of the provided day, starting at the one after the base date
		if (new Date(rollingDate.setDate(rollingDate.getDate() + 7)) <= this.endDateUTC!) {
			// Day is within start & end dates, so enable quick option
			return false
		}

		return true
	}

	getOptionCheckedValue (dayNumber: number) {
		switch (dayNumber) {
			case DayOfWeek.Monday:
				return this.areAllMondaysSelected
			case DayOfWeek.Tuesday:
				return this.areAllTuesdaysSelected
			case DayOfWeek.Wednesday:
				return this.areAllWednesdaysSelected
			case DayOfWeek.Thursday:
				return this.areAllThursdaysSelected
			case DayOfWeek.Friday:
				return this.areAllFridaysSelected
			case DayOfWeek.Saturday:
				return this.areAllSaturdaysSelected
			case DayOfWeek.Sunday:
				return this.areAllSundaysSelected
			default:
		}
	}

	bulkUpdateDays (dayNumber: number) {
		let baseDate: Date = new Date(this.startDateUTC!)

		// Set the base date to the first date required date based on the start date and the provided day number
		baseDate.setDate(baseDate.getDate() + (dayNumber - baseDate.getDay()))

		var rollingDate = new Date(baseDate)

		// Check if the option is already selected
		if (this.getOptionCheckedValue(dayNumber)) {
			// Dates are to be added

			// Check if the base date starts within the start & end dates
			if (baseDate.getTime() >= this.startDateUTC!.getTime() &&
				baseDate.getTime() <= this.endDateUTC!.getTime()) {
				// Check if the provided day starts on the startDate
				const initialIndex = this.selectedDates.findIndex((d: Date) => new Date(d).getTime() === baseDate.getTime())
				if (initialIndex < 0) {
					this.selectedDates.push(baseDate)
				}
			}

			// Loop through each instance of the provided day, starting at the one after the base date
			while (new Date(rollingDate.setDate(rollingDate.getDate() + 7)) <= this.endDateUTC!) {
				// Only add dates that haven't previously been added
				const index = this.selectedDates.findIndex((d: Date) => new Date(d).getTime() === rollingDate.getTime())
				if (index < 0) {
					this.selectedDates.push(new Date(rollingDate))
				}
			}
		} else {
			// Dates are to be removed

			// Check if the provided day starts on the startDate
			const initialIndex = this.selectedDates.findIndex((d: Date) => new Date(d).getTime() === baseDate.getTime())
			if (initialIndex >= 0) {
				this.selectedDates.splice(initialIndex, 1)
			}

			// Loop through each instance of the provided day, starting at the one after the base date
			while (new Date(rollingDate.setDate(rollingDate.getDate() + 7)) <= this.endDateUTC!) {
				// Remove existing dates
				const index = this.selectedDates.findIndex((d: Date) => new Date(d).getTime() === rollingDate.getTime())
				if (index >= 0) {
					this.selectedDates.splice(index, 1)
				}
			}
		}

		// Update Weekdays quick option
		this.areAllWeekdaysSelected = this.areAllWeekdayDatesSelected()

		// Emit value for selected dates
		this.pushValidSelectedDates()
	}

	bulkUpdateAllWeekdays () {
		let baseDate: Date = new Date(this.startDateUTC!)
		var rollingDate = new Date(baseDate)

		if (this.getWeekdayOptionCheckedValue) {
			// Dates are to be added

			// Add the first day if it is not a weekend
			if (!this.dayNumberIsAWeekend(baseDate.getDay())) {
				// Add date if not already selected
				const initialIndex = this.selectedDates.findIndex((d: Date) => new Date(d).getTime() === baseDate.getTime())
				if (initialIndex < 0) {
					this.selectedDates.push(baseDate)
				}
			}

			// Loop through each instance of the provided day, starting at the one after the base date
			while (new Date(rollingDate.setDate(rollingDate.getDate() + 1)) <= this.endDateUTC!) {
				// Skip if day is a weekend
				if (this.dayNumberIsAWeekend(rollingDate.getDay())) {
					continue
				}

				// Only add dates that haven't previously been added
				const index = this.selectedDates.findIndex((d: Date) => new Date(d).getTime() === rollingDate.getTime())
				if (index < 0) {
					this.selectedDates.push(new Date(rollingDate))
				}
			}

			// Check all weekday quick options
			this.switchAllWeekdayOptions(true)
		} else {
			// Dates are to be removed

			// Remove the first day if it is not a weekend
			if (!this.dayNumberIsAWeekend(baseDate.getDay())) {
				// Remove date if not already selected
				const initialIndex = this.selectedDates.findIndex((d: Date) => new Date(d).getTime() === baseDate.getTime())
				if (initialIndex >= 0) {
					this.selectedDates.splice(initialIndex, 1)
				}
			}

			// Loop through each instance of the provided day, starting at the one after the base date
			while (new Date(rollingDate.setDate(rollingDate.getDate() + 1)) <= this.endDateUTC!) {
				// Skip if day is a weekend
				if (this.dayNumberIsAWeekend(rollingDate.getDay())) {
					continue
				}

				// Remove existing dates
				const index = this.selectedDates.findIndex((d: Date) => new Date(d).getTime() === rollingDate.getTime())
				if (index >= 0) {
					this.selectedDates.splice(index, 1)
				}
			}

			// Uncheck all weekday quick options
			this.switchAllWeekdayOptions(false)
		}
		// Emit value for selected dates
		this.pushValidSelectedDates()
	}

	switchAllWeekdayOptions (checked: boolean) {
		if (checked === true) {
			if (this.getValidSelectedDates.some(() => this.getValidSelectedDates.find((date: Date) => new Date(date).getDay() === DayOfWeek.Monday))) {
				this.areAllMondaysSelected = checked
			}
			if (this.getValidSelectedDates.some(() => this.getValidSelectedDates.find((date: Date) => new Date(date).getDay() === DayOfWeek.Tuesday))) {
				this.areAllTuesdaysSelected = checked
			}
			if (this.getValidSelectedDates.some(() => this.getValidSelectedDates.find((date: Date) => new Date(date).getDay() === DayOfWeek.Wednesday))) {
				this.areAllWednesdaysSelected = checked
			}
			if (this.getValidSelectedDates.some(() => this.getValidSelectedDates.find((date: Date) => new Date(date).getDay() === DayOfWeek.Thursday))) {
				this.areAllThursdaysSelected = checked
			}
			if (this.getValidSelectedDates.some(() => this.getValidSelectedDates.find((date: Date) => new Date(date).getDay() === DayOfWeek.Friday))) {
				this.areAllFridaysSelected = checked
			}
		} else {
			this.areAllMondaysSelected = checked
			this.areAllTuesdaysSelected = checked
			this.areAllWednesdaysSelected = checked
			this.areAllThursdaysSelected = checked
			this.areAllFridaysSelected = checked
		}
	}

	clearAllSelectedDates () {
		// Clear selected dates
		this.selectedDates = []

		// Uncheck all quick options
		this.switchAllWeekdayOptions(false)
		this.areAllSaturdaysSelected = false
		this.areAllSundaysSelected = false
		this.areAllWeekdaysSelected = false

		// Emit value for selected dates
		this.pushValidSelectedDates()
	}

	public excludeWeekendsChanged (newValue: boolean) {
		if (newValue) {
			this.areAllSaturdaysSelected = false
			this.areAllSundaysSelected = false

			this.isSaturdayQuickOptionDisabled = true
			this.isSundayQuickOptionDisabled = true
		} else {
			this.areAllSaturdaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Saturday)
			this.areAllSundaysSelected = this.areAllDatesSelectedByDay(DayOfWeek.Sunday)

			this.isSaturdayQuickOptionDisabled = this.disableQuickOptions(DayOfWeek.Saturday)
			this.isSundayQuickOptionDisabled = this.disableQuickOptions(DayOfWeek.Sunday)
		}

		this.pushValidSelectedDates()
		this.$emit("exclude-weekends-changed", newValue)
	}

	get disabledDates () {
		if (this.isExcludeWeekendsSelected) {
			return {
				// add 1 to weekday values as component prop takes 1-7 range
				weekdays: [DayOfWeek.Saturday + 1, DayOfWeek.Sunday + 1]
			}
		}

		return []
	}

	get excludeWeekendsForCustomDatesFeatureIsEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.SchedulingExcludeWeekends) &&
			this.getIsFeatureEnabled(TenantFeatures.SchedulingExcludeWeekendsForCustomDates)
	}

	private setExcludeWeekends (newValue: boolean): void {
		this.isExcludeWeekendsSelected = newValue
		this.excludeWeekendsChanged(newValue)
	}
}
